import {
  CheckInput,
  ELoginType,
  IIMAccountInfo,
  loadingIcon,
  LocalImage,
  message,
  NextServerSideInit,
  OSType,
  postNodeLogin,
  postSendVerificationCode,
  storage,
  track,
  underscoreToCamelCase,
  usePageView,
  useAppSelector,
  getSettings,
} from '@ugc/share';
import {
  xingyeLogo,
  xingyeBg,
  xingyePhone,
  xingyeAvatar1,
  xingyeAvatar2,
  xingyeAvatar3,
  xingyeAvatar4,
  xingyeAvatar5,
} from '@ugc/share/src/assets/Login/xingye';
import cls from 'classnames';
import { GetServerSideProps } from 'next';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useState } from 'react';
import { v4 as generateUUID } from 'uuid';

import styles from './index.module.scss';

import { importSSRI18n } from '@/components/utils';

interface InputProps {
  placeholder: string;
  wrapperCls?: string;
  value: string;
  setValue: (value: string) => void;
  errMsg?: string;
  maxLength?: number;
}

const PAGE = 'ugc_login_page';

function Input({ placeholder, wrapperCls, value, setValue, errMsg, maxLength }: InputProps) {
  return (
    <div className="w-full">
      <div
        className={cls(
          'px-[18px] py-[16px] rounded-[12px] bg-[#ffffff0f] w-full box-border min-h-[54px] flex transition-all',
          wrapperCls,
          { 'border border-solid border-[#FF3D71]': !!errMsg }
        )}
      >
        <input
          maxLength={maxLength}
          className={cls('w-full text-[16px] font-normal', styles.inputNormal)}
          placeholder={placeholder}
          onChange={(e) => setValue(e.target.value)}
        />
      </div>
      {!!errMsg && <p className={cls('text-[#FF3D71] w-full', styles.textIn)}>{errMsg}</p>}
    </div>
  );
}

export default function Login() {
  const { appSettings } = useAppSelector((state) => state.global);
  const disableLoginPage = appSettings?.disable_login_page || false;
  // 下掉登录页-for安全
  useEffect(() => {
    if (disableLoginPage) {
      location.replace('https://www.xingyeai.com/'); // 跳转到首页
    }
  }, [disableLoginPage]);

  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [hasSend, setHasSent] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const [verifyErrMsg, setVerifyErrMsg] = useState('');

  usePageView(PAGE);

  useEffect(() => {
    setErrMsg('');
  }, [phoneNumber]);

  useEffect(() => {
    setVerifyErrMsg('');
  }, [verificationCode]);

  const handleLoginError = useCallback(
    ({ login_fail_type, error_msg }: { login_fail_type: string; error_msg: string }) => {
      track.collect('login_fail', PAGE, {
        login_fail_type,
        error_msg,
      });
    },
    []
  );

  const onSendVerificationCode = useCallback(() => {
    if (phoneNumber.length !== 11) {
      setErrMsg('请填写正确的手机号');
      return;
    }
    postSendVerificationCode({
      target: phoneNumber,
    })
      .then(() => {
        message.info('验证码发送成功');
        setCount(60);
        setHasSent(true);
      })
      .catch((err) => {
        if (err.status_msg) message.error(err.status_msg);
      });
  }, [phoneNumber]);

  const [agreed, setAgreed] = useState(false);

  const login = useCallback(() => {
    track.send('login_page_click', PAGE);

    if (phoneNumber.length !== 11) {
      setErrMsg('请填写正确的手机号');
      return;
    }
    if (verificationCode.length === 0) {
      setVerifyErrMsg('请填写验证码');
      return;
    }
    if (verificationCode.length !== 4) {
      setVerifyErrMsg('请填写正确的验证码');
      return;
    }
    if (!agreed) {
      message.error('请先阅读并同意用户协议与隐私政策');
      return;
    }
    return postNodeLogin({
      phone_number: phoneNumber,
      verification_code: verificationCode,
      login_type: ELoginType.PHONE_VERIFICATION_CODE,
      device_info: {
        os: OSType.WEB,
        uuid: generateUUID(),
      },
    })
      .then(async (data) => {
        storage.auth.setRefreshTokenTime();
        const { im_account_info, ...rest } = data || {};
        if (im_account_info?.account && im_account_info?.password) {
          storage.auth.saveAuthData(
            {
              imAccountInfo: data.im_account_info as IIMAccountInfo,
            },
            'xingye login'
          );
          storage.easeMob.clearToken();
        }
        // user/token相关的数据保存到storage，agent ugc业务从storage中取鉴权信息
        const camelCaseData = underscoreToCamelCase(rest);
        storage.auth.saveAuthData(
          {
            ...camelCaseData,
          },
          'xingye login'
        );
        track.send('login_success', PAGE);
        // 支持 ?redirect_url= query 跳回业务页 (例如 /agent/quick-create 匿名转登录场景),
        // 默认跳 /create 主页. 只接受同 origin 路径, 防开放重定向漏洞.
        //
        // 用 new URL(raw, origin) parse 后比对 origin: 比 startsWith('/') prefix 检查更严,
        // 能挡住 `/\evil.com` (Chrome/Safari 历史不一致处理) / `/%2F%2Fevil.com` (URL-decode
        // 后变 `///evil.com`) 等 encoded bypass 变种.
        const safeRedirect = (() => {
          if (typeof window === 'undefined') return '/create';
          const raw = new URLSearchParams(window.location.search).get('redirect_url') || '';
          if (!raw) return '/create';
          try {
            const parsed = new URL(raw, window.location.origin);
            if (parsed.origin === window.location.origin) {
              return parsed.pathname + parsed.search + parsed.hash;
            }
          } catch {
            /* parse failed → fallthrough to default */
          }
          return '/create';
        })();
        location.href = safeRedirect;
      })
      .catch((err) => {
        if (err.status_msg) {
          handleLoginError({
            login_fail_type: 'request_error',
            error_msg: err.status_msg,
          });
          message.error(err.status_msg);
        }
      });
  }, [agreed, phoneNumber, verificationCode]);

  const [count, setCount] = useState(60);

  useEffect(() => {
    if (hasSend) {
      const interval = setInterval(() => {
        setCount((currentCount) => {
          if (currentCount <= 0) {
            clearInterval(interval);
            setHasSent(false);
            return 0;
          }
          return currentCount - 1;
        });
      }, 1000);
      return () => {
        clearInterval(interval);
      };
    }
  }, [hasSend]);

  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  return (
    <div className="flex w-full h-full bg-[#000]">
      {/* 左侧 PC 装饰区: <md(768px) 隐藏, mobile 单栏 */}
      <div className="hidden md:block h-full md:w-[50vw] relative overflow-hidden">
        {/* header */}
        <div className="w-full flex px-[24px] py-[6px] items-center">
          <LocalImage className="my-[12px]" width={68} height={28} src={xingyeLogo} />
          <div className="mx-[8px] w-[1px] h-[16px] bg-[#FFFFFF33] " />
          <h1 className="text-[#FFE4A6] text-[18px] font-semibold">创作中心</h1>
        </div>
        {/* image */}
        <LocalImage className="absolute top-0 w-full h-full object-cover" src={xingyeBg} />
        <div className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 w-[80vh] mt-[40px]">
          <LocalImage
            className="absolute w-[45vh] left-0 top-1/2 -translate-y-1/2 object-cover"
            src={xingyePhone}
          />
          <div className="absolute left-[40vh] top-1/2 -translate-y-1/2 w-[32vh] h-[55vh]">
            <LocalImage
              className={cls('absolute top-0 right-0 w-[20vh]', styles.float)}
              src={xingyeAvatar1}
            />
            <LocalImage
              className={cls('absolute bottom-[28vh] left-[5.5vh] w-[8vh]', styles.float2)}
              src={xingyeAvatar2}
            />
            <LocalImage
              className={cls('absolute bottom-[15vh] left-[10vh] w-[12.5vh]', styles.float3)}
              src={xingyeAvatar3}
            />
            <LocalImage
              className={cls('absolute bottom-0 left-0 w-[13vh]', styles.float2)}
              src={xingyeAvatar4}
            />
            <LocalImage
              className={cls('absolute left-[15vh] w-[5vh] bottom-[5vh]', styles.float3)}
              src={xingyeAvatar5}
            />
          </div>
        </div>
      </div>
      {/* mobile 顶部 header (替代左侧装饰区) */}
      <div className="md:hidden absolute top-0 left-0 w-full flex px-[20px] py-[12px] items-center z-10">
        <LocalImage className="my-[8px]" width={68} height={28} src={xingyeLogo} />
        <div className="mx-[8px] w-[1px] h-[16px] bg-[#FFFFFF33] " />
        <h1 className="text-[#FFE4A6] text-[18px] font-semibold">创作中心</h1>
      </div>
      {/* 右侧表单区: mobile 全屏 + 安全内边距 */}
      <div className="flex-1 flex justify-center items-center bg-[#171717] md:rounded-[16px] px-[20px] md:px-0 pb-[60px] md:pb-0 pt-[80px] md:pt-0">
        <div className="flex justify-center items-center flex-col w-full max-w-[420px]">
          <h2 className="text-white text-[24px] font-medium mb-[40px]">{t('loginTitle')}</h2>
          <div className="flex items-center justify-center flex-col w-full gap-[12px]">
            <Input
              value={phoneNumber}
              setValue={setPhoneNumber}
              placeholder="请输入手机号"
              errMsg={errMsg}
              maxLength={11}
            />
            <div className="flex items-start w-full gap-[8px]">
              <Input
                value={verificationCode}
                setValue={setVerificationCode}
                wrapperCls="flex-1"
                placeholder="请输入验证码"
                errMsg={verifyErrMsg}
              />
              <button
                onClick={!hasSend ? onSendVerificationCode : undefined}
                className={cls('shrink-0 w-[110px] h-[54px]', styles.btnNormal, {
                  [styles.disabled]: hasSend,
                })}
              >
                {hasSend ? `${count}s` : '获取验证码'}
              </button>
            </div>
            <div className="flex items-center gap-[6px] justify-start w-full">
              <CheckInput checked={agreed} setChecked={setAgreed} />
              <div className={styles.read}>
                我已阅读并同意{' '}
                <a href={'https://m.xingyeai.com/static/service'} target="_blank">
                  用户协议
                </a>{' '}
                与{' '}
                <a href="https://m.xingyeai.com/static/privacy" target="_blank">
                  隐私政策
                </a>
              </div>
            </div>
          </div>
          <button
            onClick={async () => {
              if (loading) return;
              setLoading(true);
              try {
                await login();
              } finally {
                setLoading(false);
              }
            }}
            className={cls(
              'w-full h-[54px] mt-[32px] flex items-center justify-center',
              styles.btnNormal,
              {
                'opacity-70': loading,
              }
            )}
          >
            {loading && (
              <LocalImage className="animate-spin mr-[8px]" width={18} src={loadingIcon} />
            )}
            立即登录
          </button>
        </div>
      </div>
    </div>
  );
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { serverStore, promises } = await NextServerSideInit(context);

  const { authData } = serverStore.getState().user;

  promises.push(serverStore.dispatch(getSettings()));

  await Promise.allSettled(promises);

  return {
    props: {
      serverState: serverStore.getState(),
      ...(await importSSRI18n(context)),
    },
  };
};
